import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { buildingHomeContentApi, BuildingNewsArticle } from '../../api/buildingHomeContentApi';
import { MediaImage } from '../../components/common/MediaImage';
import { formatPublishedAtDisplay } from '../../utils/newsDateTime';
import { useCrudPermissions } from '../../hooks/useCrudPermissions';
import { BUILDING_NEWS_PERMISSIONS } from '../../constants/functionPermissions';

const statusLabel: Record<string, string> = {
  DRAFT: 'Nháp',
  PUBLISHED: 'Đã xuất bản',
  ARCHIVED: 'Lưu trữ',
};

export const BuildingNewsList: React.FC = () => {
  const { user } = useAuth();
  const { canCreate, canEdit, canDelete } = useCrudPermissions(BUILDING_NEWS_PERMISSIONS);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [articles, setArticles] = useState<BuildingNewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadArticles();
    } else {
      setArticles([]);
    }
  }, [selectedBuildingId]);

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      if (Array.isArray(response)) {
        setBuildings(response);
        setSelectedBuildingId((prev) => applyDefaultBuildingSelection(response, user, prev));
      }
    } catch {
      setError('Không thể tải danh sách tòa nhà');
    }
  };

  const loadArticles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await buildingHomeContentApi.getNewsArticles(selectedBuildingId);
      setArticles(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Không thể tải danh sách tin tức');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Xóa bài "${title}"?`)) return;
    try {
      await buildingHomeContentApi.deleteNewsArticle(id);
      await loadArticles();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Không thể xóa bài viết');
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-8">
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>Quản lý chung cư</span> / <span className="text-gray-900">Quản lý tin tức</span>
        </nav>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Quản lý tin tức</h1>
            <p className="text-gray-600">Tin tức hiển thị trên trang chủ ứng dụng mobile</p>
          </div>
          {canCreate && (
            <Link
              to="/building-news/add"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-md"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Thêm tin tức
            </Link>
          )}
        </div>
      </div>

      {shouldShowBuildingSelector(buildings) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Chọn tòa nhà:</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Chọn tòa nhà --</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800">{error}</div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-600">Đang tải...</div>
        ) : !selectedBuildingId ? (
          <div className="p-8 text-center text-gray-500">Vui lòng chọn tòa nhà</div>
        ) : articles.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có tin tức nào</h3>
            <p className="text-gray-500 mb-4">Bắt đầu bằng cách thêm bài tin tức mới</p>
            {canCreate && (
              <Link
                to="/building-news/add"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600"
              >
                Thêm tin tức
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {articles.map((article) => (
              <div key={article.id} className="p-4 flex gap-4 hover:bg-gray-50 transition-colors">
                <MediaImage
                  src={article.coverImage}
                  alt={article.title}
                  className="w-36 h-24 object-cover rounded-lg flex-shrink-0"
                  fallbackClassName="w-36 h-24 rounded-lg flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {article.isPinned && <span className="text-amber-500 mr-1">📌</span>}
                        {article.title}
                      </h3>
                      {article.summary && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{article.summary}</p>
                      )}
                      {article.publishedAt && (
                        <p className="text-xs text-gray-500 mt-2">
                          Xuất bản: {formatPublishedAtDisplay(article.publishedAt)}
                        </p>
                      )}
                    </div>
                    <span className="px-2.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 font-medium">
                      {statusLabel[article.status] || article.status}
                    </span>
                  </div>
                  <div className="flex gap-3 mt-3">
                    {canEdit && (
                      <Link
                        to={`/building-news/${article.id}/edit`}
                        className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Sửa
                      </Link>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(article.id, article.title)}
                        className="inline-flex items-center text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Xóa
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
